import { track } from './lib/telemetry.ts';

/** The "Ask it like you'd ask a colleague" section: a question pops in, the agent thinks, steps land one by one with page chips, then the source chip. Loops. */
const STEPS = [
  { n: 1, text: 'Confirm frame oil pressure is above 35 psig at the header gauge.', page: 'p. 112' },
  { n: 2, text: 'Hold the shutdown reset for 3 seconds until the panel light clears.', page: 'p. 112' },
  { n: 3, text: 'Restart and watch pressure settle between 50 and 60 psig.', page: 'p. 113' },
];
// beats: 0 empty · 1 question · 2 thinking · 3/4/5 steps · 6 source · 7 hold
const BEAT_MS = [700, 900, 1300, 700, 700, 700, 900, 4200];
const FINAL = 6;

const el = <T extends HTMLElement>(root: HTMLElement, name: string) => root.querySelector<T>(`[data-el="${name}"]`)!;

export function mountChat(section: HTMLElement): void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const question = el(section, 'chatQuestion'), thinking = el(section, 'chatThinking'), answer = el(section, 'chatAnswer');
  const steps = el<HTMLOListElement>(section, 'chatSteps'), source = el(section, 'chatSource');
  const tpl = section.querySelector<HTMLTemplateElement>('template')!;
  let timers: number[] = [];
  const clear = () => { timers.forEach(clearTimeout); timers = []; };
  const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

  // Nodes are only ever added within a cycle and cleared at its start, so an entrance animation runs once per node
  // and nothing already on screen is re-created (re-creation is what made the answer flicker).
  const paint = (beat: number) => {
    question.hidden = beat < 1;
    thinking.hidden = beat !== 2;
    answer.hidden = beat < 3;
    const wanted = Math.max(0, Math.min(beat - 2, STEPS.length));
    if (wanted < steps.children.length) steps.replaceChildren();
    for (let i = steps.children.length; i < wanted; i++) {
      const s = STEPS[i]!;
      const li = (tpl.content.firstElementChild as HTMLLIElement).cloneNode(true) as HTMLLIElement;
      el(li, 'stepN').textContent = String(s.n);
      el(li, 'stepText').textContent = s.text;
      el(li, 'stepPage').textContent = s.page;
      steps.appendChild(li);
    }
    source.hidden = beat < FINAL;
  };

  if (reduced) { paint(FINAL); return; }

  const run = (beat: number) => {
    paint(beat);
    const next = beat >= BEAT_MS.length - 1 ? 0 : beat + 1;
    later(() => run(next), BEAT_MS[beat]!);
  };
  const start = () => { clear(); run(0); };
  start();
  track('chat_demo_started');
  document.addEventListener('visibilitychange', () => { if (document.hidden) clear(); else start(); });
}
